import { jsPDF } from "jspdf";

const EVERGREEN = [14, 61, 44];
const SANDSTONE = [247, 241, 230];
const SHIMMER = [199, 178, 122];
const INK = [92, 74, 68];
const SKY = [127, 169, 201];

function drawBand(doc, y, height, rgb) {
  const width = doc.internal.pageSize.getWidth();
  doc.setFillColor(...rgb);
  doc.rect(0, y, width, height, "F");
}

export function renderReportPdf(model) {
  const doc = new jsPDF({ unit: "mm", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const maxWidth = pageWidth - margin * 2;
  let y = 0;

  function newPage() {
    doc.addPage();
    y = margin;
  }

  function ensure(space) {
    if (y + space > pageHeight - 18) newPage();
  }

  function heading(text) {
    ensure(14);
    y += 4;
    doc.setFont("times", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...EVERGREEN);
    doc.text(text, margin, y);
    y += 2;
    doc.setDrawColor(...SHIMMER);
    doc.setLineWidth(0.4);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
  }

  function body(text, { bold = false, size = 10, color = EVERGREEN } = {}) {
    const lines = doc.splitTextToSize(String(text || ""), maxWidth);
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
    lines.forEach((line) => {
      ensure(6);
      doc.text(line, margin, y);
      y += 5;
    });
  }

  drawBand(doc, 0, 32, EVERGREEN);
  doc.setFont("times", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...SKY);
  doc.text(model.title, margin, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...SHIMMER);
  doc.text(`${model.subtitle}  ·  ${model.property}`, margin, 22);
  y = 42;

  heading("File");
  const meta = [
    ["Property", model.property],
    ["Status", [model.status, model.clientRole].filter(Boolean).join(" · ")],
    ["Prepared", [model.generatedAt, model.generatedBy, model.exportType ? `${model.exportType} desk` : ""].filter(Boolean).join(" · ")],
    ["Broker", [model.parties.broker, model.partyEmails.broker].filter(Boolean).join(" · ")],
    ["Agent", [model.parties.agent, model.partyEmails.agent].filter(Boolean).join(" · ")],
    ["Client", [model.parties.client, model.partyEmails.client].filter(Boolean).join(" · ")],
    ["Phone", model.clientPhone || ""],
    ["Address", model.clientAddress || ""],
  ].filter(([, value]) => value);

  meta.forEach(([label, value]) => {
    ensure(8);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...[90, 47, 39]);
    doc.text(label, margin, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...EVERGREEN);
    const wrapped = doc.splitTextToSize(value, maxWidth - 28);
    doc.text(wrapped, margin + 28, y);
    y += Math.max(6, wrapped.length * 5);
  });

  if (model.closeTerms?.length) {
    heading("Sale close");
    model.closeTerms.forEach(([label, value]) => {
      if (!value) return;
      ensure(8);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...[90, 47, 39]);
      doc.text(label, margin, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...EVERGREEN);
      const wrapped = doc.splitTextToSize(String(value), maxWidth - 28);
      doc.text(wrapped, margin + 28, y);
      y += Math.max(6, wrapped.length * 5);
    });
  }

  heading("Timeline");
  if (!model.timeline.length) {
    body("No notes were recorded in this file.", { color: INK });
  } else {
    model.timeline.forEach((item) => {
      ensure(16);
      doc.setFillColor(...SANDSTONE);
      const preview = doc.splitTextToSize(item.text || "", maxWidth - 4);
      const blockHeight = 10 + preview.length * 5 + (item.attachments?.length ? 5 : 0);
      if (y + blockHeight > pageHeight - 18) newPage();
      doc.rect(margin - 2, y - 4, maxWidth + 4, blockHeight, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...EVERGREEN);
      doc.text(item.senderName, margin, y);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...INK);
      doc.text(item.stamp || "", pageWidth - margin, y, { align: "right" });
      y += 6;
      doc.setFontSize(10);
      doc.setTextColor(...EVERGREEN);
      preview.forEach((line) => {
        doc.text(line, margin, y);
        y += 5;
      });
      if (item.attachments?.length) {
        doc.setFontSize(8);
        doc.setTextColor(...INK);
        doc.text(item.attachments.map((file) => file.file_name || "Attachment").join(", "), margin, y);
        y += 5;
      }
      y += 4;
    });
  }

  heading("Messages");
  body(
    `${model.timeline.length} note${model.timeline.length === 1 ? "" : "s"} in this file, shown in the timeline above.`,
    { color: INK }
  );

  heading("Attachments");
  if (!model.attachments.length) {
    body("No attachments were stored with this file.", { color: INK });
  } else {
    model.attachments.forEach((file) => {
      body([file.name, file.stamp, file.type].filter(Boolean).join(" · "));
    });
  }

  heading(model.checklistHeading || (model.checklistType === "seller" ? "Seller portfolio summary" : "Buyer checklist summary"));
  if (!model.checklistSections.length) {
    body("No checklist notes were saved for this file.", { color: INK });
  } else {
    model.checklistSections.forEach((section) => {
      ensure(10);
      doc.setFont("times", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...EVERGREEN);
      doc.text(section.title, margin, y);
      y += 6;
      section.lines.forEach((line) => body(line, { size: 9 }));
      y += 2;
    });
  }

  if (model.packet) {
    heading("Notes and tips");
    if (model.specialNotes) body(model.specialNotes);
    (model.pageNotes || []).forEach((note) => body(`${note.page}: ${note.text}`, { size: 9 }));
    (model.tips || []).forEach((tip) => body(`${tip.title} — ${tip.status}`, { size: 9 }));
    if (!model.specialNotes && !model.pageNotes?.length && !model.tips?.length) {
      body("No notes or tip acknowledgements were saved on this file.", { color: INK });
    }

    heading("Transaction timeline");
    if (!model.transactionMilestones?.length) {
      body("No transaction milestones are on this file yet.", { color: INK });
    } else {
      model.transactionMilestones.forEach((item) => {
        body(`${item.title} — ${item.status}`, { bold: true, size: 10 });
        if (item.detail) body(item.detail, { size: 9, color: INK });
      });
    }

    heading("Custom steps");
    if (!model.customSteps?.length) {
      body("No custom office steps were added.", { color: INK });
    } else {
      model.customSteps.forEach((step) => {
        body(`${step.title} — ${[step.status, step.date].filter(Boolean).join(" · ")}`, { bold: true });
        if (step.description) body(step.description, { size: 9 });
        if (step.notes) body(step.notes, { size: 9, color: INK });
        if (step.attachments?.length) body(step.attachments.join(", "), { size: 8, color: INK });
      });
    }

    heading("File lifecycle");
    if (!model.lifecycle?.length) {
      body("Lifecycle stages have not been updated.", { color: INK });
    } else {
      model.lifecycle.forEach((stage, index) => {
        body(`${index + 1}. ${stage.title} — ${stage.status}`, { bold: true });
        if (stage.notes) body(stage.notes, { size: 9, color: INK });
      });
    }

    heading("Calendar");
    if (!model.calendarEvents?.length) {
      body("No calendar events are tied to this file.", { color: INK });
    } else {
      model.calendarEvents.forEach((event) => {
        body(`${event.title} — ${[event.when, event.type, event.location].filter(Boolean).join(" · ")}`);
        if (event.notes) body(event.notes, { size: 9, color: INK });
      });
    }

    heading("Action record");
    if (!model.actionTimeline?.length) {
      body("No office actions have been recorded yet.", { color: INK });
    } else {
      model.actionTimeline.forEach((item) => {
        body(`${item.title}${item.stamp ? ` · ${item.stamp}` : ""}`, { bold: true, size: 10 });
        if (item.text) body([item.source, item.text].filter(Boolean).join(" · "), { size: 9, color: INK });
      });
    }
  }

  ensure(18);
  y += 4;
  doc.setDrawColor(...SHIMMER);
  doc.line(margin, y, pageWidth - margin, y);
  y += 6;
  body(model.disclaimer, { size: 8, color: INK });

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...INK);
    doc.text(
      `${model.pageFooter || "DreamHome Journey · communication record"} · page ${i} of ${pages}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );
  }

  return doc;
}

export function downloadReportPdf(model, filename) {
  const doc = renderReportPdf(model);
  doc.save(filename);
}
